import React, { memo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import { closeAnyModal, getModalData } from "Slices/modals";
import { getMissionById } from "Slices/missionsPage/selectors";
import { getMissions as getMissionPlannerMissions } from "Slices/missionPlanner";
import { getVehicles } from "Slices/vehicles";
import { getVehicleTypes } from "Slices/vehicleTypes";
import { MissionDetailsHeader } from "./components/MissionDetailsHeader";
import { MissionDetailsTimeline } from "./components/MissionDetailsTimeline";
import { VersionHistory } from "../../MissionPlanner/components/VersionHistory";

import {
  ModalBackgroundStyled,
  ModalStyled,
} from "../../common/ModalComponent/styles";
import { MissionDetailsModalStyled } from "./styles";
import { MissionDetailsModalData } from "./types";

export const MissionDetailsModal = memo(() => {
  const dispatch = useDispatch();
  const modalData = useSelector(getModalData);

  const isValidModalData =
    !!modalData && typeof modalData === "object" && "missionId" in modalData;

  const missionId = isValidModalData
    ? (modalData as unknown as MissionDetailsModalData).missionId
    : undefined;

  // Both selectors must be called unconditionally (Rules of Hooks).
  // getMissionById handles undefined missionId gracefully.
  const missionsPageMission = useSelector(getMissionById(missionId));
  const missionPlannerMissions = useSelector(getMissionPlannerMissions);

  const vehicles = useSelector(getVehicles);
  const vehicleTypes = useSelector(getVehicleTypes);

  const handleCloseAction = useCallback(() => {
    dispatch(closeAnyModal());
  }, [dispatch]);

  if (!isValidModalData) {
    return null;
  }

  const missionPlannerMission = missionPlannerMissions?.find(
    (m) => m.id === missionId
  );

  // Use whichever slice has the mission
  const completeMissionData = missionsPageMission || missionPlannerMission;

  if (!completeMissionData) {
    return (
      <ModalStyled>
        <ModalBackgroundStyled onClick={handleCloseAction} />
        <MissionDetailsModalStyled>
          <div style={{ padding: "20px", color: "white" }}>
            Loading mission details...
          </div>
        </MissionDetailsModalStyled>
      </ModalStyled>
    );
  }

  const { stops, totalLoops, passedLoops, passedStops } = completeMissionData;
  const vehicle = vehicles.find((v) => v?.id === completeMissionData.vehicle);
  const vehicleType = vehicleTypes.find(
    (vt) => vt?.id === vehicle?.vehicleTypeId
  );

  // `mission` is the master Mission UUID on the QueuedMission that the
  // version viewset accepts. It is not part of the global Mission type
  // because that type covers both QueuedMissions and MissionAssets.
  const masterId = (completeMissionData as any).mission as string | undefined;

  return (
    <ModalStyled>
      <ModalBackgroundStyled
        onClick={handleCloseAction}
        aria-label="Close mission details modal"
        data-test-id="close-mission-details-modal"
      />
      <MissionDetailsModalStyled
        role="dialog"
        aria-modal="true"
        aria-labelledby="mission-details-title"
        aria-describedby="mission-details-content"
        data-test-id="open-mission-details-modal"
      >
        <MissionDetailsHeader
          mission={completeMissionData}
          vehicle={vehicle}
          vehicleType={vehicleType}
        />
        <div
          id="mission-details-content"
          style={{ flex: 1, minHeight: 0, overflowY: "auto" }}
        >
          <MissionDetailsTimeline
            stops={stops}
            totalLoops={totalLoops}
            passedLoops={passedLoops}
            passedStops={passedStops}
          />
          {masterId && <VersionHistory masterId={masterId} />}
        </div>
      </MissionDetailsModalStyled>
    </ModalStyled>
  );
});
